import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Team, TeamMemberRole, TeamMemberStatus } from "@/types";
import { formatUserName } from "@/utils/userName";

interface MemberTableProps {
  team: Team;
  currentUserId?: string;
  canManage: boolean;
  onRemove: (userId: string) => void;
  removingUserId?: string | null;
}

const ROLE_KEYS: Record<TeamMemberRole, string> = {
  owner: "teams.roleOwner",
  driver: "teams.roleDriver",
  engineer: "teams.roleEngineer",
  mechanic: "teams.roleMechanic",
  coach: "teams.roleCoach",
  director: "teams.roleDirector",
  other: "teams.roleOther",
};

const STATUS_KEYS: Record<TeamMemberStatus, string> = {
  active: "teams.statusActive",
  invited: "teams.statusInvited",
  former: "teams.statusFormer",
};

const STATUS_STYLES: Record<TeamMemberStatus, string> = {
  active: "bg-green-100 text-green-800",
  invited: "bg-amber-100 text-amber-800",
  former: "bg-secondary-100 text-secondary-600",
};

export default function MemberTable({
  team,
  currentUserId,
  canManage,
  onRemove,
  removingUserId,
}: MemberTableProps) {
  const { t, i18n } = useTranslation("pages");
  const [confirmingUserId, setConfirmingUserId] = useState<string | null>(null);
  const members = team.members ?? [];

  const handleAction = (userId: string) => {
    if (confirmingUserId === userId) {
      setConfirmingUserId(null);
      onRemove(userId);
    } else {
      setConfirmingUserId(userId);
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs font-semibold uppercase tracking-wide text-secondary-500 border-b border-secondary-200">
            <th className="py-2 pr-4">{t("teams.colMember")}</th>
            <th className="py-2 pr-4">{t("teams.colRole")}</th>
            <th className="py-2 pr-4">{t("teams.colStatus")}</th>
            <th className="py-2 pr-4">{t("teams.colJoined")}</th>
            <th className="py-2" />
          </tr>
        </thead>
        <tbody>
          {members.map((member) => {
            const isOwnerRow = member.userId === team.ownerId;
            const isSelf = member.userId === currentUserId;
            const showLeave = isSelf && !isOwnerRow;
            const showRemove = canManage && !isOwnerRow && !isSelf;
            const confirming = confirmingUserId === member.userId;
            const busy = removingUserId === member.userId;

            return (
              <tr
                key={member.userId}
                className="border-b border-secondary-100 last:border-b-0"
              >
                <td className="py-3 pr-4">
                  <div className="font-medium text-secondary-900">
                    {formatUserName(member.user)}
                  </div>
                </td>
                <td className="py-3 pr-4 text-secondary-600">
                  {t(ROLE_KEYS[member.role])}
                </td>
                <td className="py-3 pr-4">
                  {isOwnerRow ? (
                    <span className="inline-block rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-semibold text-primary-700">
                      {t("teams.ownerBadge")}
                    </span>
                  ) : (
                    <span
                      className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[member.status]}`}
                    >
                      {t(STATUS_KEYS[member.status])}
                    </span>
                  )}
                </td>
                <td className="py-3 pr-4 text-secondary-500">
                  {new Date(member.joinedAt).toLocaleDateString(i18n.language)}
                </td>
                <td className="py-3 text-right">
                  {(showRemove || showLeave) && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => handleAction(member.userId)}
                      onBlur={() => setConfirmingUserId(null)}
                      className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${
                        confirming
                          ? "border-red-300 bg-red-50 text-red-700"
                          : "border-secondary-300 text-secondary-700 hover:border-red-300 hover:text-red-700"
                      }`}
                    >
                      {showLeave
                        ? t(confirming ? "teams.confirmLeave" : "teams.leaveTeam")
                        : t(confirming ? "teams.confirmRemove" : "teams.remove")}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
